// Staged-upload references carried on a create request (BL-1436).
//
// A staged file is uploaded to `<tenantId>/drafts/<sessionId>/…` before the
// record that will own it exists. The create request names the staged paths it
// wants, and the server copies each one into the new record's canonical folder
// inside the same request (ADR 026 Rule 1 — one logical mutation, one request).
//
// Why these are declared EXPLICITLY on both create schemas rather than left to
// pass through: `InventoryCreatePayloadSchema` and `LotCreatePayloadSchema` are
// both `.passthrough()`, so an undeclared `stagedMedia` key would arrive
// validated by nothing at all — and then be dropped silently by the inventory
// route's explicit field allowlist. Silently dropping a photo the operator just
// uploaded is the worst available failure mode: the create succeeds, and the
// file is gone at the next sweep.
//
// Deliberately NOT the persisted shapes. A ref carries only what the client can
// legitimately know before the record exists — where the bytes are, and how the
// operator arranged them. Everything identifying (`id`, `url`, `uploadedAt`, the
// canonical `storagePath`) is minted server-side during the copy, so a client
// cannot pre-declare a media id or point a document's `url` anywhere.
//
// Pure + client-safe: no `server-only`, no I/O.

import { z } from '@auctogon/core/zod';
import { storagePathSchema } from '@auctogon/core/validation/storage-path';

/**
 * Max staged refs of each kind on one create. Mirrors the caps the persisted
 * arrays already enforce — `inventoryMediaArraySchema.max(200)` and
 * `MAX_EXTERNAL_DOCS_PER_LOT`. Kept equal on purpose: a create that validated
 * here and then failed the persisted schema would reject AFTER the copy,
 * leaving orphans for the sweep instead of a clean 400.
 */
export const MAX_STAGED_MEDIA = 200;
export const MAX_STAGED_DOCUMENTS = 50;

/**
 * One staged photo/video awaiting attachment.
 *
 * `sortOrder` + `isCover` come from the client because they describe how the
 * operator arranged the gallery, which no server-side source knows. They are
 * still normalized on write by `normalizeMediaForWrite`, which owns the
 * one-cover invariant — so a payload claiming three covers is corrected, not
 * trusted.
 */
export const StagedMediaRefSchema = z.object({
  storagePath: storagePathSchema('Staged media storage path'),
  type: z.enum(['photo', 'video']),
  caption: z.string().max(1000).optional(),
  sortOrder: z.number().int().nonnegative(),
  isCover: z.boolean(),
  /**
   * BL-2439: a staged video's poster frame, uploaded to the same draft folder.
   * The create checks it like `storagePath` (this tenant, a draft) and copies it
   * beside the video. Dropped on a photo.
   */
  posterStoragePath: storagePathSchema('Staged video poster storage path').optional(),
});

/** One staged external document awaiting attachment. */
export const StagedDocumentRefSchema = z.object({
  storagePath: storagePathSchema('Staged document storage path'),
  name: z.string().min(1, 'Document name is required').max(255),
  type: z.enum(['pdf', 'image', 'other']),
});

/** Array forms, capped. Used by both create payload schemas. */
export const StagedMediaRefArraySchema = z.array(StagedMediaRefSchema).max(MAX_STAGED_MEDIA);
export const StagedDocumentRefArraySchema = z
  .array(StagedDocumentRefSchema)
  .max(MAX_STAGED_DOCUMENTS);

export type StagedMediaRef = z.infer<typeof StagedMediaRefSchema>;
export type StagedDocumentRef = z.infer<typeof StagedDocumentRefSchema>;
